import React, { useState, useMemo, useRef } from 'react';
import { motion, useInView, AnimatePresence } from "motion/react";
import { TableIcon, UsersIcon } from '@/components/icons';
import { MediaRenderer } from './MediaRenderer';
import { PerksPopover } from './PerksPopover';
import type { ProcessedBookingCategory } from '@/features/booking/types/eventBookingCategoryTypes';
import { formatCurrencyForDisplay } from '@/lib/utils';



interface CategoryItemProps {
  category: ProcessedBookingCategory;
  index: number;
  onBook: (category: ProcessedBookingCategory) => void;
}

/**
 * Individual booking category item component
 */
export const CategoryItem: React.FC<CategoryItemProps> = ({ category, index, onBook }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPerks, setShowPerks] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  const formatPrice = (amount: number) => {
    if (!category.currency) {
      console.warn('CategoryItem: currency missing for category:', category.id);
      return `${amount / 100}`; // Show raw amount if no currency
    }
    return formatCurrencyForDisplay(amount, category.currency);
  };

  const isReversed = index % 2 !== 0;

  const tableInfo = useMemo(() => {
    if (!category.table_categories || category.table_categories.length === 0) return null;
    const totalTables = category.table_categories.reduce((acc, curr) => acc + curr.table_numbers.length, 0);
    const sizes = [...new Set(category.table_categories.map(tc => tc.table_size))].sort((a,b) => a-b).join(' & ');
    return { totalTables, sizes };
  }, [category.table_categories]);

  const hasValidImage = category.card_image_id && category.card_image_id !== '00000000-0000-0000-0000-000000000000';
  const hasValidVideo = category.card_video_id && category.card_video_id !== '00000000-0000-0000-0000-000000000000';

  return (
    <div ref={ref} className={`flex flex-col lg:flex-row items-stretch gap-0.5 my-10 ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
      {/* Media Section */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={isInView ? { opacity: 1 } : {}} 
        transition={{ duration: 0.7 }} 
        className="w-full lg:w-1/2 h-80 lg:h-auto min-h-[400px]" 
        onMouseEnter={() => setIsHovered(true)} 
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative w-full h-full overflow-hidden rounded-3xl">
          <AnimatePresence initial={false}>
            {!isHovered || !hasValidVideo ? (
              <motion.div 
                key="image" 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0 }} 
                transition={{ duration: 0.4 }} 
                className="absolute inset-0"
              >
                {hasValidImage ? (
                  <MediaRenderer 
                    mediaId={category.card_image_id} 
                    alt={category.name}
                  />
                ) : (
                  <div className="flex items-center justify-center bg-gray-900 border border-gray-800 text-gray-500 w-full h-full">
                    <span className="text-sm tracking-widest uppercase text-center p-4">
                      {category.name}
                    </span>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div 
                key="video" 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0 }} 
                transition={{ duration: 0.4 }} 
                className="absolute inset-0"
              >
                <MediaRenderer 
                  mediaId={category.card_video_id} 
                  alt={category.name}
                  isVideo={true}
                  autoPlay={true}
                  muted={true}
                  loop={true}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Details Section */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={isInView ? { opacity: 1 } : {}} 
        transition={{ duration: 0.7, delay: 0.2 }} 
        className="w-full lg:w-1/2 flex items-center"
      >
        <div className={`p-8 lg:p-16 w-full ${isReversed ? 'lg:text-right' : 'lg:text-left'}`}>
          <h2 className="text-4xl lg:text-5xl font-serif font-medium text-white mb-4">
            {category.name}
          </h2>
          {category.item_type && category.item_type !== 'UNKNOWN' && (
            <span className="inline-block px-3 py-1 mb-3 text-xs font-semibold tracking-wider uppercase bg-white/5 text-blue-400 border border-white/10 rounded-full backdrop-blur-sm">
              {category.item_type.replace('_', ' ')}
            </span>
          )}
          <p className="text-gray-400 leading-relaxed mb-6">
            {category.description}
          </p>
          
          {tableInfo && (
            <div className={`flex flex-wrap items-center gap-x-6 gap-y-2 mb-6 ${isReversed ? 'lg:justify-end' : 'lg:justify-start'}`}>
              <div className="flex items-center text-sm text-gray-300">
                <TableIcon className="w-4 h-4 mr-2 text-gray-400" />
                <span>{tableInfo.totalTables} {category.item_type && category.item_type !== 'UNKNOWN' ? `${category.item_type.replace('_', ' ').charAt(0)}${category.item_type.replace('_', ' ').slice(1).toLowerCase()}s` : 'Items'} Available</span>
              </div>
              <div className="flex items-center text-sm text-gray-300">
                <UsersIcon className="w-4 h-4 mr-2 text-gray-400" />
                <span>For {tableInfo.sizes} Guests</span>
              </div>
            </div>
          )}

          {category.capacity && (
            <div className={`mb-6 ${isReversed ? 'lg:text-right' : 'lg:text-left'}`}>
              <div className="text-sm text-gray-300">
                Capacity: {category.remaining_capacity || 0} / {category.capacity} remaining
              </div>
            </div>
          )}
          
          <div className={`flex items-center gap-6 ${isReversed ? 'lg:justify-end' : 'lg:justify-start'}`}>
            {category.perks && category.perks.length > 0 && (
              <div 
                className="relative" 
                onMouseEnter={() => setShowPerks(true)} 
                onMouseLeave={() => setShowPerks(false)}
              >
                <button className="text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                  View All Perks
                </button>
                <PerksPopover perks={category.perks} isVisible={showPerks} />
              </div>
            )}
            <button 
              onClick={() => onBook(category)}
              className="group relative px-6 py-3 bg-transparent border-2 border-gray-700 rounded-full font-semibold text-sm overflow-hidden transition-colors duration-400 ease-in-out hover:border-blue-600"
            >
              <div className="absolute top-0 left-0 h-full w-0 bg-blue-600 transition-all duration-400 ease-in-out group-hover:w-full -z-10"></div>
              <span className="relative flex items-baseline gap-4">
                <span className="text-gray-300 transition-colors duration-300 group-hover:text-white">
                  BOOK NOW
                </span>
                <span className="font-bold text-lg text-white">
                  {formatPrice(category.price)}
                </span>
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
